/**
 * DHA Partner Integration API spec, p.8:
 *
 *   "All information submitted through the APIs must be in Capital Letters"
 *
 * This applies at serialization only. The citizen sees exactly what they typed;
 * the wire sees capitals. Uppercasing form state instead would make every field
 * shout back at the user as they fill it in.
 */

/**
 * Fields that must survive byte-identical.
 *
 * `photoImg` and `signatureImg` are base64, and base64 is case-sensitive:
 * uppercasing one produces a payload that is still valid JSON, still the right
 * length, still passes a schema check — and decodes to garbage. It would ship.
 * `appRefNo` is an opaque server-issued token that we have no licence to alter.
 *
 * ⚠ THIS LIST IS MATCHED BY KEY NAME, so it is coupled to the exact casing the
 * request mappers emit. The gateway turned out to want lowerCamel rather than the
 * spec's PascalCase (see caseNormalise.ts), and when the mappers moved, this list
 * had to move with them in the same commit — otherwise `photoImg` silently stops
 * matching `PhotoImg` and every face image ships as uppercased noise.
 *
 * Both casings are listed. Not defensiveness for its own sake: the cost of a miss
 * is a corrupt biometric that passes validation, and the cost of a redundant entry
 * is one string.
 */
export const DEFAULT_PRESERVED_KEYS: readonly string[] = [
  'photoImg',
  'signatureImg',
  'fingerImg',
  'appRefNo',
  // Spec-cased forms, kept so a mapper that has not been migrated is still safe.
  'PhotoImg',
  'SignatureImg',
  'FingerImg',
  'AppRefNo',
]

export interface UppercaseOptions {
  /** Additional key names to leave untouched, merged with the defaults. */
  preserveKeys?: readonly string[]
}

/**
 * Recursively uppercases every string leaf of a JSON-serialisable value, skipping
 * the values of preserved keys. Numbers, booleans, `null` and `undefined` pass
 * through unchanged.
 */
export function deepUppercase<T>(value: T, options: UppercaseOptions = {}): T {
  const preserved = new Set([...DEFAULT_PRESERVED_KEYS, ...(options.preserveKeys ?? [])])
  return transform(value, preserved, false) as T
}

function transform(value: unknown, preserved: Set<string>, isPreserved: boolean): unknown {
  if (typeof value === 'string') {
    return isPreserved ? value : value.toUpperCase()
  }

  if (Array.isArray(value)) {
    // A preserved key holding an array preserves every element: `Fingerprints`
    // is a list of objects whose FingerImg must not be touched, and a preserved
    // key holding a list of base64 strings must not be touched either.
    return value.map((item) => transform(item, preserved, isPreserved))
  }

  if (value !== null && typeof value === 'object') {
    if (value instanceof Date) return value

    /**
     * A Blob has no own enumerable properties — they all live on the prototype — so
     * `Object.entries` below returns [] and would rewrite an image as `{}`: a request
     * that type-checks, schema-checks and uploads nothing. Binary is supposed to travel
     * in `vfsClientRequest`'s `files` option and never reach here; this is the guard that
     * keeps the failure loud if it ever does.
     */
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value

    const result: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      result[key] = transform(entry, preserved, isPreserved || preserved.has(key))
    }
    return result
  }

  return value
}
